import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import { useSelector } from 'react-redux';
import { insuranceSelector } from '../../model';

interface Props {
  intent: 'Requested Quote' | 'Providing Own Coverage';
}

export const ApplicantInsuranceIntentCard = ({ intent }: Props) => {
  const classes = useStyles();

  const chipLabel =
    intent === 'Requested Quote'
      ? 'Applicant requested an insurance quote'
      : 'Applicant indicated they will provide their own coverage';

  const proposals = useSelector(insuranceSelector.proposals);
  const insuranceBroker = useSelector(insuranceSelector.insuranceBroker);
  const selectedFinanceProgram = useSelector(insuranceSelector.selectedFinanceProgram);

  const getMoveAlongText = () => {
    const messages = [];
    if ((!proposals || proposals.length === 0) && intent === 'Requested Quote') {
      messages.push('Send an insurance quote to move this deal along.');
    }
    if (!insuranceBroker?.contactName) {
      messages.push('Add insurance agent details to proceed with COI acquisition.');
    }
    if (!selectedFinanceProgram) {
      messages.push('Select a finance program to get coverage requirements.');
    }
    return messages.map(msg => {
      return (
        <Typography key={msg} className={classes.infoText}>
          - {msg}
        </Typography>
      );
    });
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.iconBox}>
        <AssignmentIndOutlinedIcon fontSize="small" />
      </Box>

      <Box className={classes.content}>
        <Typography className={classes.title}>Applicant Insurance Intent</Typography>

        <Typography className={classes.subtext}>
          Response to:{' '}
          <span className={classes.quote}>
            "Would you like to provide your own physical damage insurance or receive a quote for coverage?"
          </span>
        </Typography>

        <Box className={classes.row}>
          <Chip
            icon={<CheckCircleIcon />}
            label={chipLabel}
            size="small"
            className={classes.successChip}
            variant="outlined"
          />

          <Box className={classes.info}>
            <InfoOutlinedIcon className={classes.infoIcon} />
            <Box>{getMoveAlongText()}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    gap: '12px',
    padding: '14px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    alignItems: 'flex-start',
  },
  iconBox: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '4px',
  },
  subtext: {
    fontSize: '12px',
    color: '#374151',
    marginBottom: '10px',
  },
  quote: {
    color: '#111827',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  successChip: {
    borderColor: '#bbf7d0',
    backgroundColor: '#ecfdf5',
    color: '#065f46',
    fontSize: '12px',
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#2563eb',
  },
  infoIcon: {
    fontSize: '16px',
  },
  infoText: {
    fontSize: '12px',
    color: '#2563eb',
  },
});
